/*
 * File: CurrencyInput.tsx
 * Author: Austin Robinson
 * Date Created: 7/9/2022
 * Copyright (c) DrivingDoc 2022
 */
import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';

import { TabPanel } from '@mui/lab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  TypographyProps,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import Typography from '@mui/material/Typography';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { useGetCoursePeriod } from 'api/hooks/private/periods/GetCoursePeriod.ts';

import getPeriodNumber from 'pages/Admin/LegacyCalendar/util/getPeriodNumber.ts';

import StudentOverviewV2 from './components/StudentOverviewV2.tsx';

export default function useDriveOverviewDialog() {
  const [open, setOpen] = useState(false);
  const [currentPeriodId, setCurrentPeriodId] = useState<string | null>(null);
  const [tab, setTab] = useState('0');
  const [studentId, setStudentId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const initialScrollPosition = useRef<number | null>(null);

  // const { data: period, isFetched }: { data: CoursePeriod, isFetched: boolean } = useQuery(['course_period', currentPeriodId], async () => getCoursePeriod({ id: currentPeriodId as string }), {
  //   enabled: currentPeriodId !== null,
  //   refetchOnWindowFocus: false,
  //   refetchOnMount: false,
  //   refetchOnReconnect: false,
  // }) as any;
  const { data: period, isFetched } = useGetCoursePeriod({
    variables: {
      id: currentPeriodId ?? '',
    },
    enabled: !!currentPeriodId,
  });

  // useEffect(() => {
  //   if (currentPeriodId) {
  //     const subscription = (API.graphql(graphqlOperation(onUpdateDrive, { id: currentPeriodId })) as any).subscribe({
  //       next: ({ value }: any) => {
  //         const updatedDrive = value.data.onUpdateDrive;
  //         queryClient.invalidateQueries(['driveOverviewDialogV2', updatedDrive.id]);
  //       },
  //       error: (error: any) => captureException(error),
  //     });
  //     return (() => {
  //       subscription.unsubscribe();
  //     });
  //   }
  //   return () => {};
  // }, [currentPeriodId]);

  const handleClose = () => {
    setOpen(false); // close the dialog
    setTab('0'); // reset tab position to 0
    // setCurrentDriveID(null); // remove the drive
    setTimeout(() => {
      // prevent chrome bug that scrolls page to top
      if (initialScrollPosition.current !== null) {
        window.scrollTo(0, initialScrollPosition.current);
        initialScrollPosition.current = null;
      }
    }, 0);
  };

  const openDriveOverviewDialog = (driveID: string, studentId?: string) => {
    initialScrollPosition.current = document.documentElement.scrollTop;
    setCurrentPeriodId(driveID);
    setOpen(true);
    if (studentId) {
      setStudentId(studentId);
    }
  };

  useEffect(() => {
    if (isFetched) {
      const tabIndex = period?.period_enrollments.findIndex((enrollment) => {
        console.log(enrollment);
        return enrollment.student_user_id === studentId;
      });
      setTab(`${tabIndex}`);
    }
  }, [studentId, isFetched]);

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderItem = (
    label: string,
    value: string,
    labelProps: TypographyProps = {},
    valueProps: TypographyProps = {},
  ) => (
    <Box key={uuidv4()} display="flex" py={0} pr={2}>
      <Typography variant="body2" fontWeight="bold" {...labelProps}>
        {label}: &nbsp;
      </Typography>
      <Typography variant="body2" fontWeight="regular" color="text" {...valueProps}>
        &nbsp;
        {value}
      </Typography>
    </Box>
  );

  const DriveOverviewDialog = useMemo(
    () => (
      <QueryClientProvider client={queryClient}>
        <Dialog
          // maxWidth="lg"
          open={open}
          onClose={handleClose}
        >
          <DialogTitle>Period Overview</DialogTitle>

          {!isFetched && (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          )}

          {isFetched && period !== null && (
            <TabContext value={tab}>
              <DialogContent
                sx={
                  {
                    // width: '50%'
                  }
                }
              >
                <Grid container overflow="hidden" direction="column">
                  <Grid item>
                    {period && renderItem('Course', period.course_section.course.title ?? '')}
                    {period && renderItem('Section', period.course_section.title ?? '')}
                    {period?.course_period_instructors.map((coursePeriodInstructor) => {
                      const { instructor } = coursePeriodInstructor;
                      return renderItem(
                        'Instructor',
                        `${instructor.user.nickname ?? instructor.user.first_name} ${instructor.user.last_name}`,
                      );
                    })}
                    {/* {renderItem('Instructor', `${drive.instructor?.firstName} ${drive.instructor?.lastName}`)} */}
                    {period && renderItem('Date', moment(period.start).format('dddd, MMMM Do'))}
                    {period &&
                      renderItem(
                        'Time',
                        `${moment(period.start).format('h:mm A')} - ${moment(period.end).format('h:mm A')}`,
                      )}
                    {/* {renderItem('Registration Open', moment(drive.openRegistration).fromNow())} */}
                    {/* {renderItem('Registration Close', moment(drive.closeRegistration).fromNow())} */}
                    {/* {renderItem('More', 'More Drive Specific Info Can Go Here If Needed')} */}
                  </Grid>
                  {/* <Grid item> */}
                  {/*  {renderItem('Registration Open', moment(drive.openRegistration).format('dddd, MMMM Do h:mm A'))} */}
                  {/*  {renderItem('Registration Close', moment(drive.closeRegistration).format('dddd, MMMM Do h:mm A'))} */}
                  {/*  /!* {renderItem('Time', `${moment(drive.start).format('h:mm A')} - ${moment(drive.end).format('h:mm A')}`)} *!/ */}
                  {/*  /!* {renderItem('More', 'More Drive Specific Info Can Go Here If Needed')} *!/ */}
                  {/* </Grid> */}
                  <Grid
                    item
                    sx={{
                      width: '100%',
                      pb: 1,
                    }}
                  >
                    {(period?.period_enrollments?.length ?? 0) > 1 && (
                      <TabList
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons={false}
                        allowScrollButtonsMobile
                      >
                        {period?.period_enrollments.map((periodEnrollment, index: number) => (
                          <Tab
                            key={`${periodEnrollment.course_period_id}${periodEnrollment.student_user_id}`}
                            label={`(${getPeriodNumber(period.id, periodEnrollment.student.period_enrollments, '#', 'Canceled')}) ${periodEnrollment.student.user.first_name} ${periodEnrollment.student.user.last_name}.`}
                            value={index.toString()}
                          />
                        ))}
                      </TabList>
                    )}
                  </Grid>

                  <Grid item>
                    {period?.period_enrollments?.map((periodEnrollment, index: number) => (
                      <TabPanel
                        key={`${periodEnrollment.course_period_id}${periodEnrollment.student_user_id}`}
                        value={index.toString()}
                        sx={{
                          p: 0,
                        }}
                      >
                        <StudentOverviewV2 periodEnrollment={periodEnrollment} />
                      </TabPanel>
                    ))}
                  </Grid>
                </Grid>
                {/*<Box display="flex" sx={{ borderBottom: 1, borderColor: 'divider', border: '2px solid green'}}>*/}
                {/*  <TabList onChange={handleTabChange} variant="scrollable" scrollButtons allowScrollButtonsMobile sx={{*/}
                {/*    border: '2px solid red',*/}
                {/*  }}>*/}
                {/*    {period.period_enrollments.map((periodEnrollment, index: number) => (*/}
                {/*        <Tab*/}
                {/*            key={`${periodEnrollment.course_period_id}${periodEnrollment.student_user_id}`}*/}
                {/*            label={`(${getDriveNumber(period.id, periodEnrollment.student.period_enrollments, '#', 'Canceled')}) ${periodEnrollment.student.user.first_name} ${periodEnrollment.student.user.last_name}.`}*/}
                {/*            // value={index.toString()}*/}
                {/*            value={index.toString()}*/}
                {/*        />*/}
                {/*    ))}*/}
                {/*  </TabList>*/}
                {/*</Box>*/}

                {period?.period_enrollments?.length === 0 && (
                  <Box display="flex" justifyContent="center" alignItems="center">
                    <Typography>No Registrations</Typography>
                  </Box>
                )}
              </DialogContent>
            </TabContext>
          )}

          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
          </DialogActions>
        </Dialog>
      </QueryClientProvider>
    ),
    [period, open, tab],
  );

  return {
    openDriveOverviewDialog,
    DriveOverviewDialog,
  };
}
